// Service Effects Handler - Handles network calls and external services
// Dispatches follow-up events (e.g., PARSE_RESULT)

import { DialogueEffect, DialogueEvent } from './types';
import { EffectHandler } from './EffectBus';
import { OPENAI_API_URL, OPENAI_MODEL, OPENAI_API_KEY, USE_MOCK_API, API_BASE_URL } from '../../../../services/apiConfig';
import { to_simple_sentences } from '../../../../services/openaiSimpleSentences';
import { extract_sentence_info } from '../../../../services/openaiValueExtraction';
import { extract_keywords } from '../DialogueManagementConversation/PerStepManagement/keywordExtraction';
import { extract_actions } from '../DialogueManagementConversation/PerStepManagement/actionExtraction';
import { isWellDefinedTask } from '../DialogueManagementDefinedTaskClassification/wellDefinedTasks';
import { Lang, t } from '../../../../i18n';
import { llmBackendClient } from '../../../../services/llmBackendClient';

export class ServiceEffectsHandler implements EffectHandler {
  private dispatch: (event: DialogueEvent) => void;
  private conversation: any;
  private getCurrentLanguage: () => Lang;

  constructor(
    dispatch: (event: DialogueEvent) => void,
    conversation?: any,
    getCurrentLanguage?: () => Lang
  ) {
    this.dispatch = dispatch;
    this.conversation = conversation;
    this.getCurrentLanguage = getCurrentLanguage || (() => 'en' as Lang);
  }

  /**
   * Fetch with retry logic for timeout errors
   * If a timeout occurs, shows a message to the user and retries after 10 seconds
   */
  private async fetchWithRetry(
    url: string,
    options: RequestInit,
    timeoutMs: number = 30000, // 30 seconds default timeout
    retryDelayMs: number = 10000 // 10 seconds retry delay
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error: any) {
      clearTimeout(timeoutId);
      
      // Check if it's a timeout/abort error
      if (error.name === 'AbortError' || error.message?.includes('timeout') || error.message?.includes('aborted')) {
        // Show message to user if conversation is available
        if (this.conversation) {
          const lang = this.getCurrentLanguage();
          const message = t(lang, 'serverBusyRetrying');
          
          // Dispatch NEW_MESSAGE event - UIEffectsHandler will add it to conversation
          this.dispatch({
            type: 'NEW_MESSAGE',
            message: {
              id: Math.random().toString(36).substr(2, 9),
              text: message,
              sender: 'Assistant',
              timestamp: new Date(),
              isAssistant: true,
              messageType: 'assistant'
            }
          });
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, retryDelayMs));
        
        // Retry once
        const retryController = new AbortController();
        const retryTimeoutId = setTimeout(() => retryController.abort(), timeoutMs);
        
        try {
          const retryResponse = await fetch(url, {
            ...options,
            signal: retryController.signal
          });
          clearTimeout(retryTimeoutId);
          return retryResponse;
        } catch (retryError: any) {
          clearTimeout(retryTimeoutId);
          throw retryError;
        }
      } else {
        // Not a timeout error, rethrow
        throw error;
      }
    }
  }

  canHandle(effect: DialogueEffect): boolean {
    return effect.type === 'CALL_LLM_PARSE' 
      || effect.type === 'PROCESS_TASK_SPLITTING'
      || effect.type === 'CHECK_SUBTASK_LEAF'
      || effect.type === 'GET_SUBTASK_VARIANTS'
      || effect.type === 'GET_SUBTASK_CHILDREN';
  }

  async handle(effect: DialogueEffect): Promise<void> {
    if (effect.type === 'CALL_LLM_PARSE') {
      await this.callLLMParse(effect);
    } else if (effect.type === 'PROCESS_TASK_SPLITTING') {
      await this.processTaskSplitting(effect);
    } else if (effect.type === 'CHECK_SUBTASK_LEAF') {
      await this.checkSubtaskLeaf(effect);
    } else if (effect.type === 'GET_SUBTASK_VARIANTS') {
      await this.getSubtaskVariants(effect);
    } else if (effect.type === 'GET_SUBTASK_CHILDREN') {
      await this.getSubtaskChildren(effect);
    }
  }

  private async callLLMParse(effect: DialogueEffect & { type: 'CALL_LLM_PARSE' }): Promise<void> {
    try {
      // ✅ SHORT-CIRCUIT: Option selection in normal task processing
      // If we're in conv_task_processing with taskState processing_options_selection,
      // and the user text exactly matches one of the available task descriptions,
      // we can skip intent classification and directly emit SELECT_OPTION.
      if (
        effect.conversationState === 'conv_task_processing' &&
        effect.taskState === 'processing_options_selection' &&
        this.conversation &&
        Array.isArray(this.conversation.tasks)
      ) {
        const activeTask = this.conversation.tasks.find((t: any) => t.isProcessingDialogue);
        const topK = activeTask?.top_k_tasks || activeTask?.topKTasks;

        if (activeTask && Array.isArray(topK) && topK.length > 0) {
          const normalizedText = (effect.text || '').toLowerCase().trim();
          const matched = topK.find((task: any) =>
            (task.description || '').toLowerCase().trim() === normalizedText
          );

          if (matched) {
            console.log('✅ [ServiceEffectsHandler] Matched user text to existing task option, bypassing LLM', {
              messageId: effect.messageId,
              text: effect.text,
              matchedTaskId: matched.task_id || matched.id,
            });

            // Update active task with the selected option so downstream
            // entry actions (ask_variant, billing, etc.) know which task
            // was chosen.
            activeTask.selectedTaskId = matched.task_id || matched.id;
            activeTask.selectedTask = matched;

            this.dispatch({
              type: 'PARSE_RESULT',
              messageId: effect.messageId,
              intentId: 'SELECT_OPTION',
              slots: {},
              confidence: 1.0,
              ambiguousWith: undefined,
            });

            return; // Exit early, don't call LLM
          }
        }
      }

      // FIRST: Check if this is a well-defined task BEFORE calling LLM
      // Only check if USER_SUBMITS_TASK is in allowed intents (meaning we're in a state where tasks can be submitted)
      if (effect.allowedIntents.includes('USER_SUBMITS_TASK')) {
        const isWellDefined = isWellDefinedTask(effect.text, effect.language as Lang);
        if (isWellDefined) {
          // Dispatch PARSE_RESULT directly with USER_SUBMITS_TASK intent
          this.dispatch({
            type: 'PARSE_RESULT',
            messageId: effect.messageId,
            intentId: 'USER_SUBMITS_TASK',
            slots: {
              task_description: effect.text
            },
            confidence: 1.0,
            ambiguousWith: undefined
          });
          
          return; // Exit early, don't call LLM
        }
      }
      
      // If mock mode is enabled, use simple mock parsing
      if (USE_MOCK_API) {
        await this.mockLLMParse(effect);
        return;
      }

      // Call the LLM Backend
      // Convert Message[] to the format expected by llm_backend
      const compactContext = (effect.compactContext || []).map(msg => ({
        role: msg.isAssistant ? 'assistant' as const : 'user' as const,
        text: msg.text || msg.textKey || ''
      }));

      const result = await llmBackendClient.classifyIntent({
        version: '1.0.0',
        messageId: effect.messageId,
        text: effect.text,
        conversationId: effect.conversationId || '',
        conversationState: effect.conversationState,
        taskState: effect.taskState,
        allowedIntents: effect.allowedIntents,
        language: (effect.language || 'en') as 'en' | 'nl',
        compactContext
      });

      // Dispatch PARSE_RESULT (regular parsing path - well-defined tasks bypass LLM earlier)
      // Note: The llm_backend only returns intentId and confidence, no slots
      // For USER_SUBMITS_TASK, extract task_description from the text
      // For ENTER_LOCATION, put user text as location (legacy / conv_task_processing)
      let slots: Record<string, any> = {};
      if (result.intentId === 'USER_SUBMITS_TASK') {
        slots.task_description = effect.text;
      }
      if (result.intentId === 'ENTER_LOCATION') {
        slots.location = effect.text;
      }

      console.log('🟢 [ServiceEffectsHandler] LLM success, dispatching PARSE_RESULT', {
        messageId: effect.messageId,
        intentId: result.intentId,
        conversationState: effect.conversationState
      });

      this.dispatch({
        type: 'PARSE_RESULT',
        messageId: effect.messageId,
        intentId: result.intentId,
        slots,
        confidence: result.confidence || 0.8,
        ambiguousWith: undefined
      });

    } catch (error) {
      console.warn('🔴 [ServiceEffectsHandler] LLM/backend failed, dispatching PARSE_ERROR', {
        messageId: effect.messageId,
        reason: error instanceof Error ? error.message : String(error)
      });
      this.dispatch({ 
        type: 'PARSE_ERROR', 
        messageId: effect.messageId, 
        reason: error instanceof Error ? error.message : 'Unknown error' 
      });
    }
  }

  private async mockLLMParse(effect: DialogueEffect & { type: 'CALL_LLM_PARSE' }): Promise<void> {
    // Simple mock parsing - map common user inputs to intents
    const text = effect.text.toLowerCase();
    let intentId = 'USER_SAYS_SOMETHING_ELSE';
    let slots: Record<string, any> = {};

    // Map common greetings
    if (text.includes('hello') || text.includes('hi') || text.includes('hey')) {
      intentId = 'USER_SAYS_HELLO';
    }
    // Map goodbye
    else if (text.includes('bye') || text.includes('goodbye') || text.includes('see you')) {
      intentId = 'USER_SAYS_GOODBYE';
    }
    // Map help requests
    else if (text.includes('help') || text.includes('what can you do')) {
      intentId = 'USER_ASKS_FOR_HELP';
    }
    // Map task intention requests (want to, intend to, need to submit)
    else if ((text.includes('want to') || text.includes('intend to') || text.includes('need to') || text.includes('would like to')) && 
             (text.includes('task') || text.includes('work') || text.includes('submit') || text.includes('report'))) {
      intentId = 'USER_WANTS_TO_SUBMIT_TASK';
      slots.task_description = effect.text;
    }
    // Map direct task submissions (already completed tasks)
    else if (text.includes('task') || text.includes('work') || text.includes('do')) {
      intentId = 'USER_SUBMITS_TASK';
      slots.task_description = effect.text;
    }
    // Map confirmations
    else if (text.includes('yes') || text.includes('confirm') || text.includes('ok')) {
      intentId = 'USER_CONFIRMS';
    }
    // Map rejections
    else if (text.includes('no') || text.includes('cancel') || text.includes('reject')) {
      intentId = 'USER_REJECTS';
    }

    // Ensure intent is in allowed intents
    if (!effect.allowedIntents.includes(intentId)) {
      intentId = effect.allowedIntents[0] || 'USER_SAYS_SOMETHING_ELSE';
    }

    // Dispatch mock PARSE_RESULT
    this.dispatch({
      type: 'PARSE_RESULT',
      messageId: effect.messageId,
      intentId,
      slots,
      confidence: 0.9,
      ambiguousWith: undefined
    });
  }

  private async processTaskSplitting(effect: DialogueEffect & { type: 'PROCESS_TASK_SPLITTING' }): Promise<void> {
    try {
      const { taskText, conversationState, language, conversationId } = effect;
      
      // STEP 1: Check if the input contains well-defined tasks
      const isWellDefined = isWellDefinedTask(taskText, language as Lang);
      
      
      let sentences: string[] = [];
      
      if (isWellDefined) {
        // For well-defined tasks, don't split - treat as single sentence
        sentences = [taskText];
      } else {
        
        // Use llm_backend for splitting
        try {
          const result = await llmBackendClient.splitTasks({
            version: '1.0.0',
            messageId: Math.random().toString(36).substr(2, 9),
            text: taskText,
            language: language as 'en' | 'nl'
          });
          
          
          // Extract sentences from the split result
          sentences = result.tasks.map((t: any) => t.description);
          
          // If no splitting occurred, treat as single sentence
          if (sentences.length === 0) {
            sentences = [taskText];
          } else {
          }
        } catch (splitError) {
          // Fall back to using the original text
          sentences = [taskText];
        }
      }

      // Step 2: Extract measurements and create Task objects for each sentence
      const newTasks: any[] = [];
      for (const sentence of sentences) {
        let measurements = undefined;
        let location = null;
        let employeeCount = null;
        
        if (USE_MOCK_API) {
          // Mock value extraction - simple pattern matching
          measurements = {
            length: null as number | null,
            area: null as number | null,
            width: null as number | null,
            diameter: null as number | null,
            depth: null as number | null,
            angle: null as number | null,
            volume: null as number | null,
            size: null as number | null,
            height: null as number | null
          };
          
          // Simple mock extraction for common patterns
          const lengthMatch = sentence.match(/(\d+(?:\.\d+)?)\s*(?:m|meter|meters|cm|centimeter|centimeters)/i);
          if (lengthMatch) {
            measurements.length = parseFloat(lengthMatch[1]);
          }
          
          const diameterMatch = sentence.match(/(\d+(?:\.\d+)?)\s*(?:mm|cm)\s*(?:diameter|d=)/i);
          if (diameterMatch) {
            measurements.diameter = parseFloat(diameterMatch[1]);
          }
          
          // Mock location extraction
          const locationMatch = sentence.match(/(?:in|at|op|in de)\s+([a-zA-Z\s]+?)(?:\s|$|,|\.)/i);
          if (locationMatch) {
            location = locationMatch[1].trim();
          }
          
          // Mock employee count
          if (sentence.toLowerCase().includes('we ') || sentence.toLowerCase().includes('wij ')) {
            employeeCount = 2;
          } else {
            employeeCount = 1;
          }
        } else {
          try {
            const info = await extract_sentence_info(sentence);
            measurements = info.measurements;
            location = info.location;
            employeeCount = info.employee_count;
          } catch (extractError) {
          }
        }
        
        const keywords = extract_keywords(sentence, language);
        const actions = extract_actions(sentence, language);

        const taskObj = {
          id: Math.random().toString(36).substr(2, 9),
          userDescription: sentence,
          measurements,
          location,
          employeeCount,
          keywords,
          actions,
          // Add default dialogue state properties
          dialogueState: 'processing_options_selection',
          isProcessingDialogue: false,
          isSubmitted: false
        };
        newTasks.push(taskObj);
      }

      // Dispatch TASK_SPLITTING_RESULT event with the processed tasks
      this.dispatch({
        type: 'TASK_SPLITTING_RESULT',
        conversationId,
        tasks: newTasks,
        originalText: taskText,
        success: true
      });

    } catch (error) {
      
      // Dispatch error event
      this.dispatch({
        type: 'TASK_SPLITTING_ERROR',
        conversationId: effect.conversationId,
        originalText: effect.taskText,
        error: error instanceof Error ? error.message : 'Unknown error',
        success: false
      });
    }
  }

  private async checkSubtaskLeaf(effect: DialogueEffect & { type: 'CHECK_SUBTASK_LEAF' }): Promise<void> {
    try {
      // Retry logic with timeout detection
      const response = await this.fetchWithRetry(
        `${API_BASE_URL}/check_task_leaf`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            task_id: effect.subtaskId,
            language: effect.language
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to check subtask leaf: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      // Always check if task has variants (even if it's a leaf, it can have variants)
      let hasVariants = false;
      let variants = null;
      let parentTask = null; // Parent task object with billing info
      
      
      try {
        const variantsResponse = await fetch(`${API_BASE_URL}/get_task_variants`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            task_id: effect.subtaskId,
            language: effect.language
          })
        });
        
        
        if (variantsResponse.ok) {
          const variantsResult = await variantsResponse.json();
          
          if (variantsResult.variants && Object.keys(variantsResult.variants).length > 0) {
            hasVariants = true;
            variants = variantsResult.variants;
          } else {
          }
        } else {
          const errorText = await variantsResponse.text();
        }
        
        // Fetch the full parent task object to get billing info (similar to regular task management)
        // This is the task that contains the variants and their billing information
        // Only fetch if we found variants
        if (hasVariants && variants) {
          try {
            const taskIdForRequest = parseInt(String(effect.subtaskId));
            if (!isNaN(taskIdForRequest)) {
              const parentTaskResponse = await fetch(`${API_BASE_URL}/task_by_id`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  task_id: taskIdForRequest,
                  language: effect.language
                })
              });
              
              if (parentTaskResponse.ok) {
                const parentTaskResult = await parentTaskResponse.json();
                // Extract the parent task object from the response
                if (parentTaskResult.parent_task) {
                  parentTask = parentTaskResult.parent_task;
                }
              }
            }
            } catch (parentTaskErr) {
            // Continue without parent task - we'll try to fetch it later if needed
          }
        }
      } catch (err) {
      }

      // Dispatch result event with parent task object
      
      this.dispatch({
        type: 'SUBTASK_LEAF_RESULT',
        subtaskId: effect.subtaskId,
        taskId: effect.taskId, // Include taskId if provided (for multi-select mode)
        isLeaf: result.is_leaf || false,
        hasVariants: hasVariants,
        hasChildren: result.has_children || false,
        variants: variants,
        parentTask: parentTask, // Include parent task object with billing info
        children: null // Will be fetched separately if needed
      });

    } catch (error) {
      // Dispatch error event
      this.dispatch({
        type: 'SUBTASK_LEAF_ERROR',
        subtaskId: effect.subtaskId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }

  private async getSubtaskVariants(effect: DialogueEffect & { type: 'GET_SUBTASK_VARIANTS' }): Promise<void> {
    try {
      
      const response = await fetch(`${API_BASE_URL}/get_task_variants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          task_id: effect.subtaskId,
          language: effect.language
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to get subtask variants: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      // Dispatch result event
      this.dispatch({
        type: 'SUBTASK_VARIANTS_RESULT',
        subtaskId: effect.subtaskId,
        variants: result.variants || {},
        variantsCount: result.variants_count || 0
      });

    } catch (error) {
      this.dispatch({
        type: 'SUBTASK_VARIANTS_ERROR',
        subtaskId: effect.subtaskId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }

  private async getSubtaskChildren(effect: DialogueEffect & { type: 'GET_SUBTASK_CHILDREN' }): Promise<void> {
    try {
      // Retry logic with timeout detection
      const response = await this.fetchWithRetry(
        `${API_BASE_URL}/task_by_id`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            task_id: parseInt(effect.subtaskId),
            language: effect.language
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get subtask children: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      // Extract children from the result - preserve both description and dutch_description
      const children = result.children || [];
      const childrenData = children.map((child: any) => {
        const childData: any = {
          id: String(child.task_id),
          description: child.description || '',
          dutch_description: child.dutch_description || ''
        };
        
        // Log what we're receiving from backend
        console.log('📥 [ServiceEffectsHandler] Child data from backend:', {
          task_id: child.task_id,
          id: child.id,
          description: child.description,
          dutch_description: child.dutch_description,
          hasDescription: 'description' in child,
          hasDutchDescription: 'dutch_description' in child,
          descriptionType: typeof child.description,
          dutchDescriptionType: typeof child.dutch_description,
          fullChildObject: child
        });
        
        return childData;
      });

      // Dispatch result event
      this.dispatch({
        type: 'SUBTASK_CHILDREN_RESULT',
        subtaskId: effect.subtaskId,
        children: childrenData
      });

    } catch (error) {
      this.dispatch({
        type: 'SUBTASK_CHILDREN_ERROR',
        subtaskId: effect.subtaskId,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }
}
